/**
 * wipeoutConverter — WIPEOUT → CadPrimitive[] (PDF-5d).
 *
 * A WIPEOUT masks the geometry beneath it by painting the PAPER BACKGROUND
 * colour (WHITE on a white plot) over a closed polygon. It is the plot analogue
 * of AutoCAD's wipeout: an opaque region that hides whatever was drawn before
 * it in draw order.
 *
 * Two primitives may be emitted (matching AutoCAD's frame toggle, DXF group
 * 280 → `frame`):
 *   1. ALWAYS — a FILL-ONLY (`stroke: false`, `fill: "nonzero"`) closed
 *      `PathPrimitive` whose style colour is the paper background = WHITE
 *      {r:1,g:1,b:1}. The backend emits `f` (nonzero fill, no boundary stroke),
 *      so the polygon is filled with white — masking everything painted under
 *      it at the wipeout's draw-order position. RAW world mm coords.
 *   2. IF `frame === true` — a STROKED boundary `PathPrimitive` (no fill) using
 *      the entity's resolved PLOT colour, so the visible wipeout frame is drawn
 *      on top of the mask. `frame === false` → mask only (no frame line).
 *
 * Draw order: the wipeout is converted at its position in `space.entities`; the
 * sheet renderer walks entities in authored order and run-length batches them
 * (`batchPaint`), so the white fill is painted AFTER the geometry it precedes
 * in the file — correctly masking it (AutoCAD draws a wipeout over earlier
 * geometry). The white fill-only path flows through `batchPaint`'s FILL path
 * (`paintMode === "fill"` → `f`).
 *
 * ── WHITE-MASK CAVEAT (documented, not a bug) ──────────────────────────────
 * On a WHITE plot page a white wipeout fill is visually invisible BUT still
 * masking: it overwrites the pixels under it with white, so geometry beneath
 * the wipeout disappears (which is exactly the wipeout's purpose). If the page
 * background were ever non-white, the mask colour would need to follow it; the
 * exporter currently plots on white paper, so white is correct.
 *
 * ── MODEL GAP (raster-image wipeout) ───────────────────────────────────────
 * AutoCAD's WIPEOUT is a raster-image-derived entity (AcDbRasterImage subclass)
 * that *can* carry an actual image; the typed `WipeoutEntity` (model.ts:512)
 * stores ONLY the clip-boundary polygon (`vertices`) + the `frame` flag — there
 * is no image payload in the model. A true raster-image wipeout (showing an
 * embedded image inside the boundary) is therefore OUT OF SCOPE: there is
 * nothing to render but the mask polygon. Completing it requires the importer /
 * model to capture the image data first (PDF-5d-followup / importer scope).
 */

import type { WipeoutEntity } from "@dxf-ai-editor/drawing-model";

import type {
  CadConvertContext,
  CadPrimitive,
  PathPrimitive,
  PathSegment,
  Point2,
} from "../cadPrimitive";
import { PLOT_WHITE } from "../pdfTransform";
import { resolvePlotStyle } from "./resolveStyle";

/**
 * Convert a WIPEOUT to its CadPrimitive list:
 *   - <3 vertices (degenerate, schema min is 3 but defend anyway) → `[]`;
 *   - else a white FILL-ONLY mask path, plus a stroked frame path when
 *     `frame === true`.
 */
export function wipeoutToPdfPrimitives(
  entity: WipeoutEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const verts = entity.vertices;
  // A wipeout needs a real polygon to mask. The schema enforces min 3, but a
  // defensive guard keeps a malformed entity from emitting a degenerate fill.
  if (!verts || verts.length < 3) return [];

  const start: Point2 = { x: verts[0]!.x, y: verts[0]!.y };
  const segments: PathSegment[] = [];
  for (let i = 1; i < verts.length; i++) {
    segments.push({ kind: "line", to: { x: verts[i]!.x, y: verts[i]!.y } });
  }

  // The entity's resolved plot style (colour / lineweight / linetype). Used as
  // the BASE for both primitives so the frame inherits the correct lineweight;
  // the mask overrides the colour to white.
  const plotStyle = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (plotStyle.skip) return [];
  const layer = entity.layer ?? "0";

  const out: CadPrimitive[] = [];

  // (1) White FILL-ONLY mask (always). The style colour is WHITE — the paper
  // background — NOT the plot-tweaked entity colour. `stroke: false` +
  // `fill: "nonzero"` → the backend paints `f` only (mask, no boundary stroke).
  const mask: PathPrimitive = {
    type: "path",
    entityId: entity.id,
    layer,
    start,
    segments,
    closed: true,
    fill: "nonzero",
    stroke: false,
    style: { ...plotStyle, color: PLOT_WHITE },
  };
  out.push(mask);

  // (2) Frame boundary (only when frame === true). Stroked, no fill, in the
  // entity's resolved PLOT colour so it reads on the white page.
  if (entity.frame === true) {
    const frame: PathPrimitive = {
      type: "path",
      entityId: entity.id,
      layer,
      start,
      segments,
      closed: true,
      fill: "none",
      // stroke defaults to true (PathPrimitive contract) → backend strokes `S`.
      style: plotStyle,
    };
    out.push(frame);
  }

  return out;
}
